// ============================================================
// UserAvatar — Avatar con foto autenticada o iniciales fallback.
// Detrás usa useFotoUsuario para resolver la URL renderizable.
// ============================================================

import React from 'react';
import { useFotoUsuario } from 'hooks/useFotoUsuario';

interface Props {
  idUsuario:  number | null | undefined;
  fotoPerfil: string | null;
  nombres:    string;
  apellidos:  string;
  size?:      number;             // px (default 40)
  className?: string;
  rounded?:   'full' | 'xl' | 'md';
}

const PALETTE: Array<{ bg: string; text: string }> = [
  { bg: 'bg-blue-100',   text: 'text-blue-700' },
  { bg: 'bg-emerald-100',text: 'text-emerald-700' },
  { bg: 'bg-amber-100',  text: 'text-amber-700' },
  { bg: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-rose-100',   text: 'text-rose-700' },
  { bg: 'bg-cyan-100',   text: 'text-cyan-700' },
];

function pickColor(seed: string) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s += seed.charCodeAt(i);
  return PALETTE[s % PALETTE.length];
}

export const UserAvatar: React.FC<Props> = ({
  idUsuario,
  fotoPerfil,
  nombres,
  apellidos,
  size = 40,
  className = '',
  rounded = 'xl',
}) => {
  const { src, loading } = useFotoUsuario(idUsuario, fotoPerfil);
  const initials = `${(nombres[0] ?? '').toUpperCase()}${(apellidos[0] ?? '').toUpperCase()}`;
  const color = pickColor(initials || String(idUsuario ?? ''));
  const radius = rounded === 'full' ? 'rounded-full' : rounded === 'md' ? 'rounded-md' : 'rounded-xl';
  const baseStyle: React.CSSProperties = { width: size, height: size };

  if (src) {
    return (
      <span className={`inline-block overflow-hidden bg-slate-100 ${radius} ${className}`} style={baseStyle}>
        <img
          src={src}
          alt={`${nombres} ${apellidos}`.trim()}
          className="w-full h-full object-cover"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center font-bold ${color.bg} ${color.text} ${radius} ${className}
        ${loading ? 'animate-pulse' : ''}`}
      style={{ ...baseStyle, fontSize: Math.max(10, Math.round(size * 0.4)) }}
      aria-label={`${nombres} ${apellidos}`.trim()}
    >
      {initials || '·'}
    </span>
  );
};

export default UserAvatar;
